import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('motobiz_token'));
  const [loading, setLoading] = useState(true);
  const [garageInfo, setGarageInfo] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('motobiz_garage')); } catch { return null; }
  });
  const [subscriptionPlan, setSubscriptionPlan] = useState(() => {
    try { return sessionStorage.getItem('motobiz_plan') || 'standard'; } catch { return 'standard'; }
  });

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Fetch garage info once alongside user (cached for session)
      if (!garageInfo) {
        fetchGarageInfo();
      }
      // Always fetch fresh subscription plan (don't rely on stale cache)
      await fetchSubscriptionPlan();
    } catch (error) {
      console.error('Auth error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const fetchGarageInfo = async () => {
    try {
      const res = await axios.get(`${API}/settings/garage`);
      setGarageInfo(res.data);
      sessionStorage.setItem('motobiz_garage', JSON.stringify(res.data));
    } catch (error) {
      // Fallback silently
    }
  };

  const refreshGarageInfo = async () => {
    await fetchGarageInfo();
    await fetchSubscriptionPlan();
  };

  const fetchSubscriptionPlan = async () => {
    try {
      const res = await axios.get(`${API}/subscription/plan`);
      const plan = res.data?.plan || 'standard';
      setSubscriptionPlan(plan);
      sessionStorage.setItem('motobiz_plan', plan);
    } catch {
      // Fallback to standard
    }
  };

  const login = async (identifier, password) => {
    // Detect if identifier is email or phone
    const isEmail = identifier.includes('@');
    const payload = isEmail ? { email: identifier, password } : { phone: identifier, password };
    const response = await axios.post(`${API}/auth/login`, payload);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('motobiz_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('motobiz_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('motobiz_token');
    sessionStorage.removeItem('motobiz_garage');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setGarageInfo(null);
    setSubscriptionPlan('standard');
    sessionStorage.removeItem('motobiz_plan');
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (typeof roles === 'string') return user.role === roles;
    return roles.includes(user.role);
  };

  const hasPermission = (moduleKey) => {
    if (!user) return false;
    // Owners and super_admins always have full access
    if (user.role === 'owner' || user.role === 'super_admin') return true;
    // If no permissions array set, deny access (except dashboard is always allowed)
    if (!user.permissions || user.permissions.length === 0) {
      return moduleKey === 'dashboard';
    }
    return user.permissions.includes(moduleKey);
  };

  const isPremium = subscriptionPlan === 'premium' || (subscriptionPlan && subscriptionPlan.includes('premium')) || (user && user.role === 'super_admin');

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, hasRole, hasPermission, garageInfo, refreshGarageInfo, subscriptionPlan, isPremium }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
